/**
 * Design JSON V2 — vocabulary (frozen constants).
 *
 * The complete set of allowed values that make up the Design JSON V2 language.
 * Every enum is frozen so the contract cannot be mutated at runtime. Later
 * phases (validator, genome, thinking, compiler) bind to these exact tokens.
 *
 * This module contains vocabulary only — no structure builders (see
 * factories.js) and no validation (Phase 2).
 */

/** Schema identity. `SCHEMA_ID` is the on-document marker; `SCHEMA_VERSION` is numeric. */
export const SCHEMA_ID = 'designjson/2';
export const SCHEMA_VERSION = 2;

/** The four node categories a section/group can contain. */
export const NODE_KINDS = Object.freeze(['object', 'group', 'component', 'frame']);

/** Primitive object types (the `type` of an `object` node). */
export const PRIMITIVE_TYPES = Object.freeze(['text', 'shape', 'image', 'icon', 'vector', 'line']);

/** Shape geometries for `shape` primitives. */
export const SHAPE_KINDS = Object.freeze([
  'rect', 'roundedRect', 'ellipse', 'polygon', 'star', 'blob', 'wave', 'custom',
]);

/** Fill layer kinds — a fill is an ordered stack of these. */
export const FILL_KINDS = Object.freeze([
  'solid', 'linearGradient', 'radialGradient', 'conicGradient', 'meshGradient',
  'image', 'pattern', 'texture', 'noise',
]);

/** Subset of FILL_KINDS that are gradients. */
export const GRADIENT_KINDS = Object.freeze([
  'linearGradient', 'radialGradient', 'conicGradient', 'meshGradient',
]);

/** Effect kinds (an object carries an ordered effect stack). */
export const EFFECT_KINDS = Object.freeze([
  'dropShadow', 'innerShadow', 'layerBlur', 'backdropBlur', 'glow', 'grain', 'overlay',
]);

/** Masking / clipping kinds. */
export const MASK_KINDS = Object.freeze(['shape', 'image', 'text', 'alpha', 'clip']);

/** Auto-layout container types. */
export const LAYOUT_TYPES = Object.freeze(['stack', 'row', 'grid', 'free']);

/** Sizing modes for auto-layout children. */
export const SIZING_MODES = Object.freeze(['hug', 'fill', 'fixed']);

/** Cross-axis alignment within an auto-layout. */
export const ALIGN_MODES = Object.freeze(['start', 'center', 'end', 'stretch', 'baseline']);

/** Main-axis distribution within an auto-layout. */
export const DISTRIBUTE_MODES = Object.freeze(['start', 'center', 'end', 'between', 'around', 'evenly']);

/** Semantic section roles. Extensible — the vocabulary may grow additively. */
export const SECTION_ROLES = Object.freeze([
  'hero', 'header', 'content', 'offer', 'features', 'gallery', 'pricing',
  'testimonial', 'stats', 'cta', 'contact', 'footer', 'decoration',
]);

/** Semantic z-bands (layers). Order here is back-to-front. */
export const LAYER_BANDS = Object.freeze(['background', 'decoration', 'content', 'foreground', 'overlay']);

/** Blend modes — the standard compositing modes shared across design tools. */
export const BLEND_MODES = Object.freeze([
  'normal', 'multiply', 'screen', 'overlay', 'darken', 'lighten',
  'color-dodge', 'color-burn', 'hard-light', 'soft-light', 'difference',
]);

/** Stroke placement relative to the path edge. */
export const STROKE_ALIGNS = Object.freeze(['inside', 'center', 'outside']);

/** Stroke dash styles. */
export const STROKE_STYLES = Object.freeze(['solid', 'dashed', 'dotted']);

/** Text case transforms. */
export const TEXT_CASES = Object.freeze(['none', 'upper', 'lower', 'title', 'sentence']);

/** Text horizontal alignment. */
export const TEXT_ALIGNS = Object.freeze(['left', 'center', 'right', 'justify']);

/** Horizontal constraint modes (how a node holds position on resize). */
export const CONSTRAINT_H = Object.freeze(['left', 'right', 'center', 'stretch', 'scale']);

/** Vertical constraint modes. */
export const CONSTRAINT_V = Object.freeze(['top', 'bottom', 'center', 'stretch', 'scale']);

/** Canvas orientation (derived from dimensions). */
export const ORIENTATIONS = Object.freeze(['portrait', 'landscape', 'square']);

/** Measurement units. */
export const UNITS = Object.freeze(['px', 'mm']);

/** Image / frame fit modes. */
export const FIT_MODES = Object.freeze(['cover', 'contain', 'fill']);

/** Token groups that make up the brand kit. */
export const TOKEN_GROUPS = Object.freeze(['color', 'type', 'space', 'radius', 'elevation', 'stroke', 'effect']);

/** Semantic color roles inside the color token group. */
export const COLOR_ROLES = Object.freeze([
  'primary', 'secondary', 'accent', 'surface', 'onSurface', 'muted', 'success', 'warning',
]);

/** Named typography styles inside the type token group. */
export const TYPE_STYLES = Object.freeze(['display', 'h1', 'h2', 'h3', 'body', 'caption', 'overline']);

/** Spacing scale steps inside the space token group. */
export const SPACE_STEPS = Object.freeze(['xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl']);
